import { useMemo } from 'react';
import useNames from './useNames';
import { chipStatus, chipTitle, attendeeNames } from '../domain/calendarGrid';

// The events in the current window, in the three shapes the views want.
//
// `occurrences` is the real shape, each carrying its recorded attendance so no
// view re-derives the lookup. `lessons` is a flattened one-row-per-student
// adaptation, which the Day view's summary adds up (`dayGlance` prices and
// counts per student). `blocks` is the same row shape one per occurrence,
// which the Week and Day time grids draw (#749): a group lesson is one block
// titled `2 students`, as MMS draws it.
//
// All views paint an event the same way (#130), so anything the paint needs is
// copied across flat rather than reached for through `_occurrence`, and a view
// cannot read one shape's field off the other.
export default function useCalendarOccurrences({
  occurrencesBetween, rangeStart, rangeEnd, statusFor, getStudent, search,
}) {
  const names = useNames();
  const occurrences = useMemo(() => {
    let list = occurrencesBetween(rangeStart, rangeEnd);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((o) => {
        const names = o.studentIds
          .map((sid) => getStudent(sid))
          .filter(Boolean)
          .map((s) => `${s.firstName || ''} ${s.lastName || ''}`)
          .join(' ');
        return `${names} ${o.categoryLabel || ''}`.toLowerCase().includes(q);
      });
    }

    return list.map((o) => ({
      ...o,
      // A group event has no single attendance state, so it reports none
      // rather than one student's. The core's rule (`grid_status` in
      // core/src/week.rs, #748), shared with the phones' Month and Week chips.
      status: chipStatus(o.studentIds.map((sid) => statusFor(o.id, sid))),
    }));
  }, [occurrencesBetween, rangeStart, rangeEnd, statusFor, search, getStudent]);

  const lessons = useMemo(
    () => occurrences.flatMap((o) =>
      (o.studentIds.length ? o.studentIds : [null]).map((sid) => {
        const student = sid ? getStudent(sid) : null;
        return {
          id: sid ? `${o.id}__${sid}` : o.id,
          occurrenceId: o.id,
          date: new Date(`${o.date}T${o.startTime || '00:00'}`),
          durationMin: o.durationMin,
          // Carried flat, like the category (#160).
          //
          // `date` is still built from `startTime` on an all-day event: the
          // flag says the clock is not to be read, not that there is no
          // instant, and sorting and `isSameDay` both need one.
          allDay: Boolean(o.allDay),
          // A money field (#362): the day summary asks `isBillableFor(status,
          // this)`, because an event carrying `This event requires a make-up
          // credit` makes `Unrecorded` not billable.
          requiresMakeupCredit: o.requiresMakeupCredit === true,
          // `occurrencesBetween` in domain/schedule.js stamps `pendingWrite`
          // onto the occurrence (#407), where a series' pending state and its
          // override's meet. `=== true`, because occurrences built by hand (the
          // calendar views' tests) leave it `undefined`.
          pendingWrite: o.pendingWrite === true,
          studentId: sid,
          studentName: student ? names.student(student) : o.categoryLabel,
          status: sid ? statusFor(o.id, sid) : 'unrecorded',
          // The category, carried whole (#130): the label is the studio's own,
          // as written.
          categoryId: o.categoryId ?? null,
          categoryLabel: o.categoryLabel || '',
          // No `cancelled` flag (#152): a removed entry is a rule date in the
          // series' `exceptions` and never reaches this flattening, and one
          // called off says so through `status`.
          note: o.privateDescription,
          location: null,
          recurringRuleId: o.seriesId,
          priceMinor: o.priceOverrideMinor,
          _occurrence: o,
        };
      })
    ),
    [occurrences, getStudent, statusFor, names]
  );

  // One row per occurrence, for the time grids (#749). The fields are a
  // lesson row's, so a card, the drags and the popover read either; what
  // differs is that the row is the occurrence's own:
  //
  //   * `id` is the occurrence id, unique in a window, so a drag finds it;
  //   * `studentId` is the one student's, kept so the card's name follows a
  //     rename live, and null for a group or an empty lesson;
  //   * `studentName` is the core's chip title (`chip_title` in
  //     core/src/week.rs): the name, `2 students`, or the category label —
  //     the same title the Month chip and both phones write;
  //   * `attendeeNames` is who a group is, for a screen reader;
  //   * `status` is the occurrence's one status, `unrecorded` for a group.
  //
  // `startTime` is carried flat because the lane layout is the core's, and it
  // places a block from the stored wall-clock time rather than from `date`.
  const blocks = useMemo(
    () => occurrences.map((o) => {
      const attendees = o.studentIds.map((sid) => {
        const student = getStudent(sid);
        return student ? names.student(student) : null;
      });
      return {
        id: o.id,
        occurrenceId: o.id,
        date: new Date(`${o.date}T${o.startTime || '00:00'}`),
        startTime: o.startTime || null,
        durationMin: o.durationMin,
        allDay: Boolean(o.allDay),
        requiresMakeupCredit: o.requiresMakeupCredit === true,
        pendingWrite: o.pendingWrite === true,
        studentId: o.studentIds.length === 1 ? o.studentIds[0] : null,
        studentIds: o.studentIds,
        studentName: chipTitle(attendees, o.categoryLabel || ''),
        attendeeNames: attendees.length > 1 ? attendeeNames(attendees) : '',
        status: o.status,
        categoryId: o.categoryId ?? null,
        categoryLabel: o.categoryLabel || '',
        note: o.privateDescription,
        location: null,
        recurringRuleId: o.seriesId,
        priceMinor: o.priceOverrideMinor,
        _occurrence: o,
      };
    }),
    [occurrences, getStudent, names]
  );

  return { occurrences, lessons, blocks };
}
